import React, { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TopicDeleteConfirm } from './TopicDeleteConfirm';
import { TopicModal } from './TopicModal';
import { TopicContextModal } from './TopicContextModal';
import { TopicItemMenu } from './TopicItemMenu';
import { TOPIC_ICONS } from '../../constants/icons';
import { TOPIC_COLORS } from './topicColors';
import { useViewport } from '../../contexts/ViewportContext';
import { useDeleteTopic, useUpdateTopic } from '../../hooks/queries/useTopics';
import { FormattedTopic } from '../../hooks/queries/topicTypes';

/**
 * Helper function to get the appropriate icon component for a topic type
 */
export function getTopicIcon(topicType: string) {
  return TOPIC_ICONS[topicType as keyof typeof TOPIC_ICONS]?.icon;
}

interface TopicItemProps {
  topic: FormattedTopic;
  isActive: boolean;
  isListHovered?: boolean;
  onClick: () => void;
}

export function TopicItem({ topic, isActive, isListHovered = false, onClick }: TopicItemProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [isItemHovered, setIsItemHovered] = useState(false);
  const { isMobile } = useViewport();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  
  // Use React Query hooks for CRUD operations
  const deleteMutation = useDeleteTopic();
  const updateMutation = useUpdateTopic();
  

  // Get type icon from the shared constant
  const TypeIcon = TOPIC_ICONS[topic.topicType as keyof typeof TOPIC_ICONS]?.icon || TOPIC_ICONS.default.icon;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(topic.id);
    } catch (error) {
      console.error('Error deleting topic:', error);
    }
  };

  const handleEdit = async (name: string, description: string) => {
    try {
      await updateMutation.mutateAsync({
        topicId: topic.id,
        updates: {
          name,
          description,
          lastUsedAt: new Date().toISOString(),
        }
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating topic:', error);
      throw error;
    }
  };

  const handleContextUpdate = async (context: string) => {
    try {
      await updateMutation.mutateAsync({
        topicId: topic.id,
        updates: {
          context,
          lastUsedAt: new Date().toISOString(),
        }
      });
      setIsContextModalOpen(false);
    } catch (error) {
      console.error('Error updating topic context:', error);
      throw error;
    }
  };

  return (
    <>
      <div
        className={cn(
          'relative py-3 px-4 hover:bg-gray-50 transition-colors cursor-pointer bg-transparent rounded-lg overflow-hidden group'
        )}
        onClick={handleClick}
        onMouseEnter={() => setIsItemHovered(true)}
        onMouseLeave={() => setIsItemHovered(false)}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
              isActive
                ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.active ||
                    TOPIC_COLORS.default.active
                : isMobile
                  ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.bgHover ||
                    TOPIC_COLORS.default.bgHover
                  : isListHovered
                    ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.bgHover ||
                      TOPIC_COLORS.default.bgHover
                    : TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.bgDefault ||
                      TOPIC_COLORS.default.bgDefault
            )}
            data-topic-type={topic.topicType}
          >
            <TypeIcon
              size={20}
              className={cn(
                isActive
                  ? ''
                  : isMobile
                    ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.icon ||
                      TOPIC_COLORS.default.icon
                    : isListHovered
                      ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.icon ||
                        TOPIC_COLORS.default.icon
                      : TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.defaultIcon ||
                        TOPIC_COLORS.default.defaultIcon
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className={cn(
                'font-medium text-base truncate font-montserrat',
                isActive && 'font-bold',
                isMobile && 'text-lg'
              )}
              style={{ color: 'var(--text-primary)' }}
            >
              {topic.name}
            </h3>
            {topic.description && (
              <p className="text-sm text-gray-500 truncate">{topic.description}</p>
            )}
          </div>

          <div className="flex items-center">
            <div className="relative">
              <button
                ref={buttonRef}
                onClick={e => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className={cn(
                  'p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors',
                  // Only hide on desktop when not hovered
                  !isMobile && 'opacity-0 group-hover:opacity-100',
                  // Always visible on mobile, when item is hovered, or when menu is open
                  (isMobile || isItemHovered || isMenuOpen) && 'opacity-100',
                  isMenuOpen && 'bg-gray-100'
                )}
                aria-label="Topic menu"
              >
                <MoreVertical size={16} />
              </button>

              <TopicItemMenu
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                onEdit={() => {
                  setIsMenuOpen(false);
                  setIsEditing(true);
                }}
                onDelete={() => {
                  setIsMenuOpen(false);
                  setIsDeleting(true);
                }}
                onContext={() => {
                  setIsMenuOpen(false);
                  setIsContextModalOpen(true);
                }}
                isAIChat={topic.topicType === 'ai_chat'}
                triggerRef={buttonRef}
              />
            </div>
          </div>
        </div>

        {isDeleting && (
          <TopicDeleteConfirm 
            onConfirm={handleDelete} 
            onCancel={() => setIsDeleting(false)} 
          />
        )}
      </div>

      <TopicModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        topic={topic}
        onSubmit={handleEdit}
      />

      {topic.topicType === 'ai_chat' && (
        <TopicContextModal
          isOpen={isContextModalOpen}
          onClose={() => setIsContextModalOpen(false)}
          topic={topic}
          onSubmit={handleContextUpdate}
        />
      )}
    </>
  );
}